import type { DateiVerknuepfung, Target } from "@kundeninfo/api-client";
import { LIQUIDITY_TEXT_WEB } from "../liquidity/liquidityTexts.js";
import type { AkteSprung } from "../KundenAkte.js";
import { bezugAusAkteZiel } from "../../components/objektPanel/objektBezug.js";
import type { ObjektBezug } from "../../components/objektPanel/objektBezug.js";

/** Die Wege, auf denen die Akte ein Sprungziel der Übersicht ausführt. */
export type TargetHandlers = {
  /** Der Kunde der Akte — Ziele mit Reiter (`TAB`) liegen in ihr. */
  customerId: string;
  /** Ein Objekt oder ein Reiter der Akte: Panel, wenn es eines gibt, sonst Sprung (`oeffneOderSpringe`, F16). */
  oeffne(b: ObjektBezug): void;
  onKunde(customerId: string): void;
};

/**
 * Übersetzt das Sprungziel einer Zeile — an dieser einen Stelle — in einen der Wege. Zustandslos: Jede Zeile trägt ihr
 * eigenes Ziel, nachgeschlagen wird nichts (Spec „Sprünge“). Ein Ziel mit Reiter wird zum Bezug (`bezugAusAkteZiel`,
 * PF4): Liefervertrag und Eingangsrechnung öffnen als Panel über der Akte, alles andere — Beleg, Notiz, Wichtiges, Datei
 * samt Ordner — springt wie bisher mit Vorgang bzw. Ordner; das Unternehmen des Ziels reist in der Route mit und wird dort
 * zugeschaltet. `label` ist die Beschriftung der Zeile (Titel des Panels, F13).
 */
export function followTarget(target: Target, label: string, h: TargetHandlers): void {
  if (target.kind === "TICKET") h.oeffne({ art: "ticket", id: target.ticketId });
  else if (target.kind === "CUSTOMER") h.onKunde(target.customerId);
  else {
    h.oeffne(bezugAusAkteZiel({ customerId: h.customerId, tab: target.tab,
      ...(target.objectId === undefined ? {} : { objectId: target.objectId }),
      ...(target.companyId === undefined ? {} : { companyId: target.companyId }),
      ...(target.folderId === undefined ? {} : { folderId: target.folderId }) }, label));
  }
}

/** Der Reiter der Akte, in dem ein Vorgang dieser Art steht (Chip eines Vorgangs an einer Datei, Aufgabe 25; seit
 *  Lieferanten Aufgabe 22 auch Liefervertrag und Eingangsrechnung — der Server liefert ihre Verknüpfungen nur mit
 *  Lieferantensicht, Entscheid 496). Eine Art ohne Reiter stünde als Kennzeichen da, nicht als Knopf. */
export const DOCUMENT_TAB: Readonly<Partial<Record<DateiVerknuepfung["kind"],
  "angebote" | "auftraege" | "rechnungen" | "liefervertraege" | "eingangsrechnungen">>> =
  { OFFER: "angebote", SALES_ORDER: "auftraege", INVOICE: "rechnungen", SUPPLIER_CONTRACT: "liefervertraege",
    INCOMING_INVOICE: "eingangsrechnungen" };

/** Der Satz über einem Vorgang, den ein Sprung aus der Übersicht, vom Chip im Reiter „Dateien“, aus dem Bezug eines
 *  Tickets, aus den Terminen (Liste, Zeitachse, Kalender; Terminebene T20), aus dem Cockpit (K27) oder aus der Liquidität aufgeschlagen hat. */
export const JUMP_NOTE: Readonly<Record<Exclude<AkteSprung["source"], "search" | "document">, string>> = {
  overview: "Aus der Kundenübersicht geöffnet.",
  files: "Aus dem Reiter Dateien geöffnet.",
  ticket: "Aus dem Ticket geöffnet.",
  calendar: "Aus den Terminen geöffnet.",
  cockpit: "Aus dem Cockpit geöffnet.",
  liquidity: LIQUIDITY_TEXT_WEB.jumpNote,
};

/** Der Satz je Quelle des Sprungs; ohne Quelle, aus der Suche bzw. dem Bereich (oder aus dem Abschnitt „Dateien“, der
 *  nur Ordner aufschlägt) der Satz der Suche, den der Reiter selbst nennt. */
export function jumpNote(source: AkteSprung["source"] | undefined, search: string): string {
  return source === undefined || source === "search" || source === "document" ? search : JUMP_NOTE[source];
}
